import React from "react";

export interface Crumb {
  label: string;
  to?: string;
}

export interface PrimaryAction {
  label: string;
  icon: "upload" | "add";
  onClick: () => void;
}

export interface SearchItem {
  key: string;
  label: string;
  secondary?: string;
  group: "Projects" | "Variants" | "Builds";
  to: string;
}

interface ChromeState {
  crumbs: Crumb[];
  primaryAction: { label: string; icon: PrimaryAction["icon"] } | null;
  searchItems: SearchItem[];
}

interface PageChromeContextValue {
  state: ChromeState;
  setState: React.Dispatch<React.SetStateAction<ChromeState>>;
  handlerRef: React.MutableRefObject<(() => void) | null>;
}

const EMPTY_STATE: ChromeState = { crumbs: [], primaryAction: null, searchItems: [] };

const PageChromeContext = React.createContext<PageChromeContextValue | null>(
  null
);

export const PageChromeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = React.useState<ChromeState>(EMPTY_STATE);
  const handlerRef = React.useRef<(() => void) | null>(null);
  const value = React.useMemo(
    () => ({ state, setState, handlerRef }),
    [state]
  );
  return (
    <PageChromeContext.Provider value={value}>
      {children}
    </PageChromeContext.Provider>
  );
};

/** Pages call this once per render. Last-mounted page wins; reset on unmount. */
export function usePageChrome(
  crumbs: Crumb[],
  primaryAction?: PrimaryAction,
  searchItems?: SearchItem[]
): void {
  const ctx = React.useContext(PageChromeContext);
  if (!ctx) {
    throw new Error("usePageChrome must be used inside PageChromeProvider");
  }
  const { setState, handlerRef } = ctx;
  handlerRef.current = primaryAction?.onClick ?? null;

  const crumbsKey = JSON.stringify(crumbs);
  const label = primaryAction?.label;
  const icon = primaryAction?.icon;

  React.useEffect(() => {
    setState({
      crumbs: JSON.parse(crumbsKey) as Crumb[],
      primaryAction: label && icon ? { label, icon } : null,
      searchItems: searchItems ?? [],
    });
    return () => setState(EMPTY_STATE);
  }, [crumbsKey, label, icon, searchItems, setState]);
}

/** For TopBar. primaryAction.onClick always calls the latest page handler. */
export function usePageChromeState(): {
  crumbs: Crumb[];
  primaryAction: PrimaryAction | null;
  searchItems: SearchItem[];
} {
  const ctx = React.useContext(PageChromeContext);
  if (!ctx) {
    throw new Error("usePageChromeState must be used inside PageChromeProvider");
  }
  const { state, handlerRef } = ctx;
  return {
    crumbs: state.crumbs,
    searchItems: state.searchItems,
    primaryAction: state.primaryAction
      ? { ...state.primaryAction, onClick: () => handlerRef.current?.() }
      : null,
  };
}
